import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { authService } from '../services';
import { useAuth } from '../context/AuthContext';
import './Login.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
const GOOGLE_REDIRECT_URI = process.env.REACT_APP_GOOGLE_REDIRECT_URI || 'https://marketingpro.app.br/auth/google/callback';

function FacebookIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path fill="#1877F2" d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073c0 6.025 4.388 11.018 10.125 11.927v-8.437H7.078v-3.49h3.047V9.41c0-3.026 1.792-4.697 4.533-4.697 1.312 0 2.686.236 2.686.236v2.971H15.83c-1.491 0-1.956.93-1.956 1.886v2.267h3.328l-.532 3.49h-2.796v8.437C19.612 23.091 24 18.098 24 12.073z" />
    </svg>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true" focusable="false">
      <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303C33.655,32.657,29.221,36,24,36c-6.627,0-12-5.373-12-12s5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24s8.955,20,20,20s20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z" />
      <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z" />
      <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.143,35.091,26.715,36,24,36c-5.2,0-9.623-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z" />
      <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.084,5.571c0.001-0.001,6.19,5.238,6.19,5.238C36.971,39.206,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z" />
    </svg>
  );
}

function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [fbLoading, setFbLoading] = useState(false);

  const getGoogleRedirectUri = () => GOOGLE_REDIRECT_URI;

  const handleFacebookLogin = () => {
    setError('');
    setFbLoading(true);
    window.location.assign(API_URL.replace('/api', '') + '/auth/facebook');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setLoading(true);

    try {
      const data = await authService.login({ email, password });
      if (!data?.success) {
        setError(data?.message || 'Falha ao entrar');
      } else {
        login(data);
        navigate('/dashboard');
      }
    } catch (requestError) {
      setError('Erro de conexão');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setError('');
    setGoogleLoading(true);

    try {
      const data = await authService.googleAuth(getGoogleRedirectUri());
      if (!data?.success || !data?.auth_url) {
        setError(data?.message || 'Falha ao iniciar login com Google');
        return;
      }

      window.location.assign(data.auth_url);
    } catch (requestError) {
      setError('Erro de conexão no login com Google');
    } finally {
      setGoogleLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-shell">
        <aside className="login-hero">
          <div className="login-hero-overlay" />
          <div className="login-hero-content">
            <span className="login-hero-tag">Marketing PRO</span>
            <h1>Entre na sua conta</h1>
            <p>Ambiente modular com fluxo de criação e gestão.</p>
          </div>
        </aside>

        <section className="login-panel">
          <header className="login-header">
            <div className="logo-container">
              <img src="/logo-branca.png" alt="Marketing PRO" className="logo-image" />
            </div>
          </header>

          {error && <div className="error-message">{error}</div>}

          <form className="login-form" onSubmit={handleSubmit}>
            <div className="input-group">
              <label htmlFor="email">E-mail</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                required
              />
            </div>
            <div className="input-group">
              <label htmlFor="password">Senha</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
              />
            </div>
            <button className="btn btn-outline btn-full" type="submit" disabled={loading}>
              <span className="inner">{loading ? 'Entrando...' : 'Entrar'}</span>
            </button>

            <div className="divider auth-divider"><span>ou</span></div>

            <button className="btn btn-full btn-google" type="button" onClick={handleGoogleLogin} disabled={googleLoading}>
              <span className="inner">
                <GoogleIcon />
                {googleLoading ? 'Conectando com Google...' : 'Continuar com Google'}
              </span>
            </button>

            <button className="btn btn-full btn-facebook" type="button" onClick={handleFacebookLogin} disabled={fbLoading}>
              <span className="inner">
                <FacebookIcon />
                {fbLoading ? 'Conectando com Facebook...' : 'Continuar com Facebook'}
              </span>
            </button>
          </form>

          <footer className="login-footer">
            <div className="signup-link">
              Não tem uma conta? <Link to="/signup">Criar conta</Link>
            </div>
          </footer>
        </section>
      </div>
    </div>
  );
}

export default Login;
